import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { ASSETS, NAV_LINKS, STORE_LINKS } from "../../data/landing";
import { useLanguage } from "../../i18n/LanguageContext";

const LangToggle = ({ testid = "lang-toggle" }) => {
  const { lang, setLang } = useLanguage();
  return (
    <div
      data-testid={testid}
      className="relative inline-flex items-center rounded-full border border-[#7B1113]/15 bg-white/70 p-0.5 backdrop-blur"
      role="group"
      aria-label="Language toggle"
    >
      <button
        onClick={() => setLang("en")}
        data-testid={`${testid}-en`}
        aria-pressed={lang === "en"}
        className={`relative z-10 rounded-full px-3 py-1 text-xs font-semibold transition-colors duration-300 ${
          lang === "en" ? "text-[#FCFAF7]" : "text-[#7B1113]"
        }`}
      >
        EN
      </button>
      <button
        onClick={() => setLang("hi")}
        data-testid={`${testid}-hi`}
        aria-pressed={lang === "hi"}
        className={`relative z-10 rounded-full px-3 py-1 text-xs font-semibold transition-colors duration-300 ${
          lang === "hi" ? "text-[#FCFAF7]" : "text-[#7B1113]"
        }`}
      >
        हिं
      </button>
      <motion.span
        className="pointer-events-none absolute top-0.5 bottom-0.5 z-0 rounded-full bg-[#7B1113]"
        style={{ width: "calc(50% - 2px)" }}
        animate={{ left: lang === "en" ? "2px" : "50%" }}
        transition={{ type: "spring", stiffness: 400, damping: 32 }}
      />
    </div>
  );
};

export const Navbar = () => {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 inset-x-0 z-50"
      data-testid="main-navbar"
    >
      <div
        className={`transition-[background-color,backdrop-filter,box-shadow,border-color] duration-500 ${
          scrolled
            ? "backdrop-blur-2xl bg-[#FCFAF7]/80 border-b border-[#7B1113]/8 shadow-[0_8px_30px_-12px_rgba(123,17,19,0.10)]"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-6 md:px-12 h-[76px] flex items-center justify-between">
          <a
            href="#top"
            data-testid="nav-logo"
            aria-label={`${t.brand} home`}
            className="flex items-center gap-3"
          >
            <img
              src={ASSETS.logo}
              alt={t.brand}
              width="44"
              height="44"
              className="h-11 w-11 rounded-full object-cover shadow-[0_6px_16px_-6px_rgba(123,17,19,0.4)]"
            />
            <span className="hidden sm:block text-[17px] font-semibold tracking-tight text-[#2A1112]">
              {t.brand}
            </span>
          </a>

          <div className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <a
                key={link.key}
                href={link.href}
                data-testid={`nav-link-${link.key}`}
                className="relative px-4 py-2 text-[15px] font-medium text-[#5E4646] hover:text-[#7B1113] transition-colors duration-300 group"
              >
                {t.nav[link.key]}
                <span className="absolute left-4 right-4 -bottom-0.5 h-px origin-left scale-x-0 bg-[#7B1113] transition-transform duration-300 group-hover:scale-x-100" />
              </a>
            ))}
          </div>

          <div className="flex items-center gap-2.5">
            <LangToggle />
            <motion.a
              href={STORE_LINKS.googlePlay}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-download-btn"
              aria-label={t.nav.downloadApp}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.97 }}
              className="hidden sm:inline-flex items-center rounded-full bg-[#7B1113] px-5 py-2.5 text-sm font-semibold text-[#FCFAF7] shadow-[0_12px_26px_-12px_rgba(123,17,19,0.5)] transition-shadow duration-300 hover:shadow-[0_18px_34px_-14px_rgba(123,17,19,0.6)]"
            >
              {t.nav.downloadApp}
            </motion.a>

            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="nav-mobile-toggle"
              aria-label="Toggle navigation menu"
              className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#7B1113]/12 bg-white/60 text-[#7B1113] backdrop-blur"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </nav>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden backdrop-blur-2xl bg-[#FCFAF7]/95 border-b border-[#7B1113]/8"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.key}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${link.key}`}
                  className="py-3 text-lg font-medium text-[#2A1112] border-b border-[#7B1113]/6"
                >
                  {t.nav[link.key]}
                </a>
              ))}
              <a
                href={STORE_LINKS.googlePlay}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-download-btn"
                className="mt-4 inline-flex items-center justify-center rounded-full bg-[#7B1113] px-5 py-3.5 text-base font-semibold text-[#FCFAF7]"
              >
                {t.nav.downloadApp}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
